/**
 * Email Accounts Page
 *
 * Main page for managing OAuth2 email accounts.
 * Allows admins to create, connect, reconnect, set default, and delete email accounts.
 */

import { Card, Group, Text, Button, Stack } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconPlus } from "@tabler/icons-react";
import { useCallback, useState } from "react";
import { Navigate } from "react-router-dom";
import { useGetAuthorizations } from "@/hooks/useGetAuthorizations";
import { EmailAccountsTable } from "@/modules/email-accounts/components/email-accounts-table";
import { useEmailAccounts } from "@/modules/email-accounts/hooks/useEmailAccounts";
import { CreateEmailAccountModal } from "@/modules/email-accounts/modals/create-email-account-modal";
import { OAuth2AuthorizationModal } from "@/modules/email-accounts/modals/oauth2-authorization-modal";
import type { EmailAccount, EmailAccountFormData } from "@/modules/email-accounts/schema/email-account.types";
import useUserStore from "@/modules/users/store";
import { ID_CONFIGURACAO_CONTAS_EMAIL } from "@/utils/constants";

const UNKNOWN_ERROR_MESSAGE = "Erro desconhecido";

/**
 * EmailAccountsPage Component
 *
 * Displays the main email accounts management interface.
 */
export default function EmailAccountsPage() {
  const { read: hasReadPermission } = useGetAuthorizations(ID_CONFIGURACAO_CONTAS_EMAIL);
  const activeLicenciadaId = useUserStore((state) => state.activeLicenciadaId);

  // State for modals
  const [createModalOpened, setCreateModalOpened] = useState(false);
  const [oauth2ModalOpened, setOAuth2ModalOpened] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);

  // Fetch email accounts with real-time updates
  const { accounts, loading, error, createAccount, deleteAccount, setAsDefault, refresh } = useEmailAccounts(
    activeLicenciadaId || "",
    true
  );

  /**
   * Handle create email account
   * @param data
   */
  const handleCreateAccount = useCallback(
    async (data: EmailAccountFormData): Promise<string> => {
      try {
        // Create account in Firestore (status: pending)
        const accountId = await createAccount(data);

        // Close create modal
        setCreateModalOpened(false);

        // Open OAuth2 authorization modal
        setSelectedAccountId(accountId);
        setOAuth2ModalOpened(true);

        return accountId;
      } catch (error) {
        console.error("Failed to create email account:", error);
        notifications.show({
          title: "Erro ao criar conta",
          message: error instanceof Error ? error.message : UNKNOWN_ERROR_MESSAGE,
          color: "red"
        });
        throw error;
      }
    },
    [createAccount]
  );

  /**
   * Handle reconnect account (expired/error status)
   * @param account
   */
  const handleReconnect = useCallback((account: EmailAccount) => {
    setSelectedAccountId(account.id);
    setOAuth2ModalOpened(true);
  }, []);

  /**
   * Handle delete account
   * @param account
   */
  const handleDelete = useCallback(
    (account: EmailAccount) => {
      deleteAccount(account.id)
        .then(() => {
          notifications.show({
            title: "Conta removida",
            message: `A conta ${account.email} foi removida com sucesso`,
            color: "green"
          });
          return null;
        })
        .catch((error) => {
          console.error("Failed to delete email account:", error);
          notifications.show({
            title: "Erro ao remover conta",
            message: error instanceof Error ? error.message : UNKNOWN_ERROR_MESSAGE,
            color: "red"
          });
        });
    },
    [deleteAccount]
  );

  /**
   * Handle set as default
   * @param account
   */
  const handleSetDefault = useCallback(
    (account: EmailAccount) => {
      setAsDefault(account.id)
        .then(() => {
          notifications.show({
            title: "Conta padrão atualizada",
            message: `${account.email} agora é a conta padrão`,
            color: "green"
          });
          return null;
        })
        .catch((error) => {
          console.error("Failed to set default account:", error);
          notifications.show({
            title: "Erro ao definir conta padrão",
            message: error instanceof Error ? error.message : UNKNOWN_ERROR_MESSAGE,
            color: "red"
          });
        });
    },
    [setAsDefault]
  );

  /**
   * Handle OAuth2 modal close
   */
  const handleOAuth2Close = useCallback(() => {
    setOAuth2ModalOpened(false);
    setSelectedAccountId(null);
  }, []);

  /**
   * Handle create modal open
   */
  const handleCreateModalOpen = useCallback(() => {
    setCreateModalOpened(true);
  }, []);

  /**
   * Handle create modal close
   */
  const handleCreateModalClose = useCallback(() => {
    setCreateModalOpened(false);
  }, []);

  if (!hasReadPermission) {
    return <Navigate to="/" replace />;
  }
  /**
   * Render error state
   */
  if (error && !loading) {
    return (
      <Card withBorder shadow="md">
        <Stack align="center" gap="md" py="xl">
          <Text c="red" fw={500}>
            Erro ao carregar contas de email
          </Text>
          <Text size="sm" c="dimmed">
            {error.message}
          </Text>
          <Button variant="light" onClick={refresh}>
            Tentar Novamente
          </Button>
        </Stack>
      </Card>
    );
  }

  return (
    <>
      <Card
        withBorder
        shadow="md"
        styles={{
          section: {
            overflow: "auto"
          }
        }}
      >
        {/* Header */}
        <Card.Section px="md" py="xs" withBorder>
          <Group justify="space-between">
            <Text fw={500} fz="lg">
              Contas de Email
            </Text>
            <Button leftSection={<IconPlus size="1rem" />} onClick={handleCreateModalOpen} size="sm">
              Nova Conta
            </Button>
          </Group>
        </Card.Section>

        {/* Table */}
        <Card.Section>
          <EmailAccountsTable
            accounts={accounts}
            loading={loading}
            onReconnect={handleReconnect}
            onDelete={handleDelete}
            onSetDefault={handleSetDefault}
          />
        </Card.Section>
      </Card>

      {/* Create Email Account Modal */}
      <CreateEmailAccountModal
        opened={createModalOpened}
        onClose={handleCreateModalClose}
        onCreate={handleCreateAccount}
      />

      {/* OAuth2 Authorization Modal */}
      <OAuth2AuthorizationModal opened={oauth2ModalOpened} onClose={handleOAuth2Close} accountId={selectedAccountId} />
    </>
  );
}
